import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
} from 'react-query';
import api from '../services/apiService';
import { AxiosError } from 'axios';
import qs from 'qs';
import { notification } from 'antd';

/**
 * Interface representing an API response.
 * @template T - The type of the data in the response.
 */
interface ApiResponse<T> {
  return: T;
  message: string;
  code: number;
  error: boolean;
  redirect_to: string | null;
}

/**
 * Interface representing an API error.
 */
class ApiError extends Error {
  message: string;
  code: number;
  redirect_to: string | null;

  constructor(message: string, code: number, redirect_to: string | null) {
    super(message);
    this.message = message;
    this.code = code;
    this.redirect_to = redirect_to;
  }
}

/**
 * Interface representing request options.
 */
interface RequestOptions {
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * Interface representing options for useGet hook.
 */
export interface UseGetOptions extends UseQueryOptions<any, AxiosError> {
  queryKey?: QueryKey;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * Interface representing options for mutation hooks.
 * Extends UseMutationOptions to include headers.
 */
interface UseMutationWithHeadersOptions<T, D> extends UseMutationOptions<
  T,
  AxiosError,
  D
> {
  headers?: Record<string, string>;
  timeout?: number;
  /**
   * Query keys to invalidate after a successful mutation. Keys prefix-match,
   * so ['/v1/auth/reg/students'] refreshes every students query.
   */
  invalidates?: QueryKey[];
}

// Older builds stored the raw username/password under 'auth'; never keep it.
localStorage.removeItem('auth');

/**
 * Generates the authorization header from the session token in local storage.
 * @returns The authorization header as a string or null if not available.
 */
export const generateAuthorizationHeader = (): string | null => {
  const token = localStorage.getItem('token');
  return token ? `Bearer ${token}` : null;
};

/**
 * A 401 on a request that carried a session token means the session expired
 * or was revoked. Clear it and send the parent to the login page once,
 * instead of letting each screen show its own error.
 */
export const handleExpiredSession = (error: any, hadToken: boolean): void => {
  if (!hadToken || error?.response?.status !== 401) return;
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
};

/**
 * Merges provided headers with the authorization header.
 * @param headers - Optional headers to merge.
 * @returns A merged headers object.
 */
const getHeaders = (
  headers?: Record<string, string>
): Record<string, string> => {
  const authHeader = generateAuthorizationHeader();
  return {
    ...headers,
    ...(authHeader ? { Authorization: authHeader } : {}),
  };
};

/**
 * Fetches data from the given endpoint.
 * @template T - The type of the data to fetch.
 * @param endpoint - The API endpoint to fetch data from.
 * @param options - Optional request options.
 * @returns The fetched data.
 */
export const fetcher = async <T>(
  endpoint: string,
  options?: RequestOptions
): Promise<T> => {
  const queryString = options?.params ? `?${qs.stringify(options.params)}` : '';
  const hadToken = !!localStorage.getItem('token');
  try {
    const response = await api.get<ApiResponse<T>>(
      `${endpoint}${queryString}`,
      {
        headers: getHeaders(options?.headers),
      }
    );
    if (response.data.error) {
      throw new ApiError(
        response.data.message,
        response.data.code,
        response.data.redirect_to
      );
    }
    return response.data.return;
  } catch (error: any) {
    // Log the error with request context
    await logErrorWithContext(error, 'GET', endpoint, options);
    handleExpiredSession(error, hadToken);
    throw error;
  }
};

/**
 * Handles HTTP requests.
 * @template T - The type of the data to return.
 * @template D - The type of the data to send.
 * @param method - The HTTP method (post, patch, put, delete).
 * @param endpoint - The API endpoint.
 * @param data - The data to send.
 * @param options - Optional request options.
 * @returns The response data.
 */
export const requestHandler = async <T, D>(
  method: 'post' | 'patch' | 'put' | 'delete',
  endpoint: string,
  data?: D,
  options?: RequestOptions
): Promise<T> => {
  const hadToken = !!localStorage.getItem('token');
  const config = {
    headers: getHeaders(options?.headers),
    ...(options?.timeout ? { timeout: options.timeout } : {}),
  };

  try {
    const response =
      method === 'delete'
        ? await api.delete<ApiResponse<T>>(endpoint, config)
        : await api[method]<ApiResponse<T>>(endpoint, data, config);

    if (response.data.error) {
      throw new ApiError(
        response.data.message,
        response.data.code,
        response.data.redirect_to
      );
    }
    return response.data.return;
  } catch (error: any) {
    // Log the error with request context
    await logErrorWithContext(error, method, endpoint, options);
    handleExpiredSession(error, hadToken);
    throw error;
  }
};

/**
 * Logs an API error with request context information
 * @param error - The error that occurred
 * @param method - The HTTP method used
 * @param endpoint - The API endpoint
 * @param options - The request options
 */
const logErrorWithContext = async (
  error: any,
  method: string,
  endpoint: string,
  options?: RequestOptions
): Promise<void> => {
  try {
    // Don't log errors when calling the logging endpoint to avoid infinite loops
    if (endpoint.includes('/google/logging/')) {
      return;
    }

    // Use the same api instance to ensure the correct domain is used
    await api.post(
      '/v1/public/google/logging/frontend-errors',
      {
        error: {
          name: error.name || 'ApiError',
          message: error.message || 'Unknown error',
          stack: error.stack,
          status: error.response?.status,
          statusText: error.response?.statusText,
        },
        context: {
          request: {
            method,
            url: endpoint,
            params: options?.params,
            headers: options?.headers,
          },
          // Login validation errors can echo the submitted password.
          response: endpoint.includes('/login')
            ? undefined
            : error.response?.data,
        },
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
      {
        headers: getHeaders(),
      }
    );
  } catch (loggingError) {
    // Silent fail - we don't want logging errors to cause more errors
    console.error('Failed to log error:', loggingError);
  }
};

/**
 * React hook for performing a GET request with authorization.
 * @template T - The type of the data to fetch.
 * @param endpoint - The API endpoint.
 * @param options - Optional query options.
 * @returns The query result.
 */
const useQueryWithAuth = <T>(
  endpoint: string,
  options?: UseGetOptions
): UseQueryResult<T, AxiosError> => {
  const queryKey = options?.queryKey || [endpoint, options?.params];
  return useQuery<T, AxiosError>(
    queryKey,
    () =>
      fetcher<T>(endpoint, {
        params: options?.params,
        headers: options?.headers,
      }),
    {
      staleTime: 60000, // Data is fresh for 1 minute
      cacheTime: 300000, // Cache kept for 5 minutes
      refetchOnWindowFocus: false, // Prevent refetching when window regains focus
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      retry: (failureCount, error) => {
        // Only retry server errors (5xx) and limit to 2 retries
        return (
          failureCount < 2 &&
          (error.response?.status || 0) >= 500 &&
          (error.response?.status || 0) < 600
        );
      },
      ...options,
    }
  );
};

/**
 * React hook for performing a mutation request with authorization.
 * @template T - The type of the response data.
 * @template D - The type of the data to send.
 * @param method - The HTTP method (post, patch, put, delete).
 * @param endpoint - The API endpoint.
 * @param options - Optional mutation options.
 * @returns The mutation result.
 */
const useMutationWithAuth = <T, D>(
  method: 'post' | 'patch' | 'put' | 'delete',
  endpoint: string,
  options?: UseMutationWithHeadersOptions<T, D>
): UseMutationResult<T, AxiosError, D> => {
  const queryClient = useQueryClient();
  const { invalidates, onSuccess, headers, timeout, ...rest } = options || {};
  const mergedOptions: UseMutationOptions<T, AxiosError, D> = {
    // Mutations (registration, payment) are not idempotent; a retry after a
    // gateway 5xx can create duplicates.
    retry: false,
    ...rest,
    onSuccess: async (data, variables, context) => {
      invalidates?.forEach((key) => queryClient.invalidateQueries(key));
      return onSuccess?.(data, variables, context);
    },
  };
  return useMutation<T, AxiosError, D>(
    (data: D) =>
      requestHandler<T, D>(method, endpoint, data, {
        headers,
        timeout,
      }),
    mergedOptions
  );
};

export const useGet = useQueryWithAuth;

export const usePost = <T, D>(
  endpoint: string,
  options?: UseMutationWithHeadersOptions<T, D>
) => useMutationWithAuth('post', endpoint, options);

export const usePatch = <T, D>(
  endpoint: string,
  options?: UseMutationWithHeadersOptions<T, D>
) => useMutationWithAuth('patch', endpoint, options);

export const usePut = <T, D>(
  endpoint: string,
  options?: UseMutationWithHeadersOptions<T, D>
) => useMutationWithAuth('put', endpoint, options);

export const useDelete = <T>(
  endpoint: string,
  options?: UseMutationWithHeadersOptions<T, void>
) => useMutationWithAuth('delete', endpoint, options);

/**
 * Retrieves the error message from a mutation error object.
 *
 * @param {any} mutationError - The error object from a mutation.
 * @returns {string | null} - The error message, or null if not available.
 */
export const getErrorMessage = (mutationError: any): string | null => {
  return mutationError?.response?.data?.message || null;
};

/**
 * Displays a notification based on the mutation status.
 *
 * @param {any} mutation - The mutation object containing the status and error.
 * @param {Object} [options] - Optional configuration for success and error messages.
 * @param {Object} [options.success] - Success message configuration.
 * @param {string} [options.success.title='Success'] - Title for the success message.
 * @param {string} [options.success.description='Operation completed successfully.'] - Description for the success message.
 * @param {Object} [options.error] - Error message configuration.
 * @param {string} [options.error.title='Error'] - Title for the error message.
 * @param {string} [options.error.description='An error occurred. Please try again.'] - Description for the error message.
 */
export const mutationNotification = (
  mutation: any,
  options: {
    success?: { title?: string; description?: string };
    error?: { title?: string; description?: string };
  } = {
    success: {
      title: 'Success',
      description: 'Operation completed successfully.',
    },
    error: {
      title: 'Error',
      description: 'An error occurred. Please try again.',
    },
  }
) => {
  if (mutation.isSuccess) {
    notification.success({
      message: options.success?.title || 'Success',
      description:
        options.success?.description || 'Operation completed successfully.',
      placement: 'topRight',
    });
  }

  if (mutation.isError) {
    notification.error({
      message: options.error?.title || 'Error',
      description:
        getErrorMessage(mutation.error) ||
        options.error?.description ||
        'An error occurred. Please try again.',
      placement: 'topRight',
    });
  }
};

export const getDataFromApiError = (
  apiCall: UseMutationResult<any, AxiosError>
): any | null => {
  return apiCall.error?.response?.data || null;
};

/**
 * Logs frontend errors to the backend logging service.
 *
 * @param {Error} error - The error object to log
 * @param {Object} context - Additional context information
 * @param {Object} context.request - Request details (url, method, etc.)
 * @param {Object} context.body - Request body if applicable
 * @param {Object} context.query - Request query parameters
 * @param {Object} context.args - Any additional arguments
 * @param {Object} context.user - User information if available
 */
export const logFrontendError = async (
  error: Error,
  context: {
    request?: any;
    body?: any;
    query?: any;
    args?: any;
    user?: any;
    [key: string]: any;
  } = {}
): Promise<void> => {
  try {
    await api.post(
      '/v1/public/google/logging/frontend-errors',
      {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        context,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
      {
        headers: getHeaders(),
      }
    );
  } catch (loggingError) {
    // Silent fail - we don't want logging errors to cause more errors
    console.error('Failed to log error:', loggingError);
  }
};
